import React, {
  useEffect, useState, useMemo,
} from 'react';
import PropTypes from 'prop-types';
import AmountCard from './AmountCards';
import useStyles from '../styles';
import formatValueAsUSD from '../../../../../shared/helpers/formatValueAsUSD';
import getDefaultFundAllocations from '../../helpers/allocations';

function AmountBoxes({
  investmentAmounts,
  selectedCardIndex,
  handleCardClick,
  customInvestmentAmount,
  setCustomInvestmentAmount,
  setFormData,
  onUpdateAllocation,
}) {
  return investmentAmounts.map((amountBox, index) => {
    const isCustom = amountBox.title === 'Custom';

    if (isCustom) {
      return (
        <AmountCard
          key={amountBox.displayOrder}
          title={amountBox.title}
          custom
          helptext={amountBox.helpText}
          selected={selectedCardIndex === index}
          investmentAmount={customInvestmentAmount}
          setInvestmentAmount={setCustomInvestmentAmount}
          onClick={() => handleCardClick(index, customInvestmentAmount)}
          setFormData={setFormData}
          onUpdateAllocation={onUpdateAllocation}
        />
      );
    }

    return (
      <AmountCard
        key={amountBox.displayOrder}
        title={amountBox.title}
        amount={amountBox.amount}
        superscript={amountBox.superscript}
        custom={false}
        selected={selectedCardIndex === index}
        onClick={() => handleCardClick(index, amountBox.amount)}
      />
    );
  });
}

export default function SelectInvestmentAmount({
  selectInvestmentAmount,
  formData,
  setFormData,
  onUpdateAllocation,
}) {
  const classes = useStyles();
  const [selectedCardIndex, setSelectedCardIndex] = useState(null);
  const [customInvestmentAmount, setCustomInvestmentAmount] = useState();
  const [errorMessage, setErrorMessage] = useState('');

  const investmentAmounts = useMemo(() => {
    const { customInvestmentBox, diamondClubBox, investmentBoxes } = selectInvestmentAmount;

    return [...investmentBoxes, customInvestmentBox, diamondClubBox]
      .sort((a, b) => {
        // if display order is not set for both, item that has display order will be first
        if (!a.displayOrder) { return -1; }
        if (!b.displayOrder) { return 1; }

        return a.displayOrder - b.displayOrder;
      });
  }, [selectInvestmentAmount]);

  useEffect(() => {
    const formdataAmount = parseFloat(formData.amount);
    if (!formdataAmount) {
      const { minimumInvestmentAmount = 0 } = selectInvestmentAmount.customInvestmentBox;
      setErrorMessage(`The minimum allocation amount is ${formatValueAsUSD(minimumInvestmentAmount)}`);
      return;
    }

    const { customInvestmentBox } = selectInvestmentAmount;
    const amountBoxIndex = investmentAmounts.findIndex(
      (box) => box !== customInvestmentBox
        && parseFloat(box.amount) === formdataAmount,
    );

    if (amountBoxIndex >= 0) {
      setSelectedCardIndex(amountBoxIndex);
      setCustomInvestmentAmount(undefined);
      setErrorMessage('');
    } else {
      setSelectedCardIndex(investmentAmounts.indexOf(customInvestmentBox));
      setCustomInvestmentAmount(formdataAmount);

      const { minimumInvestmentAmount = 0, maximumInvestmentAmount } = selectInvestmentAmount.customInvestmentBox;
      if (formdataAmount < minimumInvestmentAmount) {
        setErrorMessage(`The minimum allocation amount is ${formatValueAsUSD(minimumInvestmentAmount)}`);
      } else if (maximumInvestmentAmount != null && formdataAmount > maximumInvestmentAmount) {
        setErrorMessage(`The maximum allocation amount is ${formatValueAsUSD(maximumInvestmentAmount)}`);
      } else {
        setErrorMessage('');
      }
    }
  }, [formData.amount, investmentAmounts, selectInvestmentAmount]);

  const handleCardClick = (index, amount) => {
    // If clicked card is already selected, unselect the card
    if (selectedCardIndex === index) {
      setFormData((prevData) => {
        const updatedFormData = {
          ...prevData,
          amount: 0,
          coreFundAllocation: '',
          focusedFundAllocation: '',
          followOnFundAllocation: '',
          syndicationFundAllocation: '',
        };
        onUpdateAllocation(updatedFormData);
        return updatedFormData;
      });
      if (investmentAmounts[index] === selectInvestmentAmount.customInvestmentBox) {
        setCustomInvestmentAmount(undefined);
      }
      setSelectedCardIndex(null);
      return;
    }

    const hasAmount = amount !== undefined && amount !== null && amount !== '';
    setFormData((prevData) => {
      const updatedFormData = {
        ...prevData,
        amount: hasAmount ? amount : '',
        ...getDefaultFundAllocations(hasAmount ? amount : 0),
      };
      onUpdateAllocation(updatedFormData);
      return updatedFormData;
    });
    setSelectedCardIndex(index);
  };

  return (
    <div className={classes.selectInvestmentAmountWrapper}>
      <div className={classes.investmentWrapper}>
        <AmountBoxes
          investmentAmounts={investmentAmounts}
          customInvestmentAmount={customInvestmentAmount}
          handleCardClick={handleCardClick}
          selectedCardIndex={selectedCardIndex}
          setCustomInvestmentAmount={setCustomInvestmentAmount}
          setFormData={setFormData}
          onUpdateAllocation={onUpdateAllocation}
        />
      </div>
      {errorMessage && (
        <div className={classes.errorMessage}>{errorMessage}</div>
      )}
    </div>
  );
}

const amountBoxShape = PropTypes.shape({
  amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  displayOrder: PropTypes.number,
  title: PropTypes.string,
});

SelectInvestmentAmount.propTypes = {
  selectInvestmentAmount: PropTypes.shape({
    customInvestmentBox: PropTypes.shape({
      displayOrder: PropTypes.number,
      helpText: PropTypes.string,
      maximumInvestmentAmount: PropTypes.number,
      minimumInvestmentAmount: PropTypes.number,
      title: PropTypes.string,
    }).isRequired,
    diamondClubBox: amountBoxShape.isRequired,
    investmentBoxes: PropTypes.arrayOf(amountBoxShape).isRequired,
  }).isRequired,
  formData: PropTypes.shape({
    amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  }).isRequired,
  setFormData: PropTypes.func.isRequired,
  onUpdateAllocation: PropTypes.func.isRequired,
};
